import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash2 } from 'lucide-react';
import { adminQueries, keys } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { CONTACT_KIND_LABELS, CONTACT_KINDS, hasPermission, labelOf, PARTY_SOURCE_LABELS, partySchema } from '@blimo/shared';
import {
  applyProblemToForm,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CheckboxField,
  ErrorState,
  Form,
  FormRootError,
  InputField,
  PageHeader,
  SelectField,
  TextareaField,
  ToggleGroup,
  toast,
} from '@blimo/ui';
import { DetailSkeleton, ReadOnlyHint } from '../../components/data.jsx';
import { ForbiddenPage } from '../pages.jsx';
import { emptyContact, formToPartyPayload, isImportedParty, isPartyEditable, partyToForm } from './party-utils.js';

const KIND_OPTIONS = CONTACT_KINDS.map((value) => ({ value, label: CONTACT_KIND_LABELS[value] }));
const TYPE_OPTIONS = [
  { value: 'person', label: 'Person' },
  { value: 'organization', label: 'Organisation' },
];

/** POST /admin/parties */
export function PartyCreatePage() {
  const { data: me } = useMe();
  if (!hasPermission(me, 'parties.create')) return <ForbiddenPage />;
  return (
    <>
      <PageHeader breadcrumb={<Breadcrumb />} title="Person oder Organisation anlegen" />
      <PartyForm party={null} />
    </>
  );
}

/** PATCH /admin/parties/{id} – only for manually maintained parties. */
export function PartyEditPage() {
  const api = useApi();
  const { data: me } = useMe();
  const { partyId } = useParams({ strict: false });
  const { data: party, error, isPending, refetch } = useQuery(adminQueries.party(api, partyId));

  if (!hasPermission(me, 'parties.update')) return <ForbiddenPage />;
  if (error) {
    return (
      <>
        <PageHeader breadcrumb={<Breadcrumb />} title="Bearbeiten" />
        <ErrorState error={error} onRetry={() => refetch()} notFoundTitle="Person bzw. Organisation nicht gefunden" />
      </>
    );
  }
  if (isPending) return <DetailSkeleton />;

  return (
    <>
      <PageHeader breadcrumb={<Breadcrumb />} title={`${party.display_name || `#${party.id}`} bearbeiten`} />
      {isPartyEditable(party) ? (
        <PartyForm party={party} />
      ) : (
        <div className="flex flex-col gap-3">
          <ReadOnlyHint>
            {isImportedParty(party)
              ? `Dieser Datensatz stammt aus ${labelOf(PARTY_SOURCE_LABELS, party.source)} und ist schreibgeschützt. Änderungen bitte im Quellsystem vornehmen.`
              : 'Dieser Datensatz ist für Sie schreibgeschützt.'}
          </ReadOnlyHint>
          <Button asChild variant="outline" className="w-fit">
            <Link to="/personen/$partyId" params={{ partyId: String(party.id) }}>
              Zurück zur Ansicht
            </Link>
          </Button>
        </div>
      )}
    </>
  );
}

function Breadcrumb() {
  return (
    <Link to="/personen" className="hover:underline">
      Personen & Organisationen
    </Link>
  );
}

/** @param {{ party: any | null }} props */
export function PartyForm({ party }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const create = !party;
  const form = useForm({ resolver: zodResolver(partySchema), defaultValues: partyToForm(party) });
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'contacts' });
  const type = useWatch({ control: form.control, name: 'type' });
  const isPerson = type === 'person';
  const sourceContacts = (party?.contacts ?? []).filter((c) => c.source && c.source !== 'manual');

  const mutation = useMutation({
    mutationFn: (payload) => (create ? api.admin.parties.create(payload) : api.admin.parties.update(party.id, payload)),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['blimo', 'admin', 'parties', 'list'] });
      queryClient.invalidateQueries({ queryKey: keys.admin.detail('parties', saved.id) });
      toast.success(create ? 'Angelegt' : 'Gespeichert');
      navigate({ to: '/personen/$partyId', params: { partyId: String(saved.id) } });
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await mutation.mutateAsync(formToPartyPayload(values, { create }));
    } catch (error) {
      applyProblemToForm(error, form.setError);
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} noValidate className="flex max-w-3xl flex-col gap-4">
        <FormRootError />
        <Card>
          <CardHeader>
            <CardTitle as="h2">Stammdaten</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {create && (
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium" aria-hidden="true">
                  Art
                </span>
                <ToggleGroup
                  label="Art"
                  size="default"
                  value={type}
                  onValueChange={(v) => form.setValue('type', v, { shouldValidate: form.formState.isSubmitted })}
                  options={TYPE_OPTIONS}
                />
              </div>
            )}
            {isPerson ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <InputField name="salutation" label="Anrede" autoComplete="off" />
                <InputField name="title" label="Titel" autoComplete="off" />
                <InputField name="first_name" label="Vorname" autoComplete="off" />
                <InputField name="last_name" label="Nachname *" autoComplete="off" />
                <InputField name="birthday" label="Geburtstag" type="date" />
                <InputField name="organization_name" label="Organisation (optional)" autoComplete="off" />
              </div>
            ) : (
              <InputField name="organization_name" label="Name der Organisation *" autoComplete="off" />
            )}
            <InputField name="letter_salutation" label="Briefanrede" description="z. B. „Sehr geehrte Frau Dr. Muster“" autoComplete="off" />
            <TextareaField name="notes" label="Notizen" rows={4} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle as="h2">Kontaktdaten</CardTitle>
            {!create && (
              <CardDescription>
                Hier gepflegte Kontakte ersetzen die bisherigen manuellen Kontakte. Kontakte aus Quellsystemen bleiben erhalten.
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {sourceContacts.length > 0 && (
              <div className="rounded-md border bg-muted/40 p-3 text-sm">
                <p className="mb-1 font-medium">Aus Quellsystemen (nicht änderbar)</p>
                <ul className="flex flex-col gap-0.5">
                  {sourceContacts.map((c) => (
                    <li key={c.id}>
                      {labelOf(CONTACT_KIND_LABELS, c.kind)}: <span className="break-all">{c.value}</span>{' '}
                      <span className="text-xs text-muted-foreground">({labelOf(PARTY_SOURCE_LABELS, c.source)})</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {fields.length === 0 && <p className="text-sm text-muted-foreground">Noch keine Kontaktdaten erfasst.</p>}
            <ul className="flex flex-col gap-3">
              {fields.map((field, index) => (
                <li key={field.id} className="grid gap-2 rounded-md border p-3 sm:grid-cols-[9rem_1fr_9rem_auto] sm:items-start">
                  <SelectField name={`contacts.${index}.kind`} label={`Art (Kontakt ${index + 1})`} options={KIND_OPTIONS} />
                  <InputField name={`contacts.${index}.value`} label={`Wert (Kontakt ${index + 1})`} autoComplete="off" />
                  <InputField name={`contacts.${index}.label`} label="Bezeichnung" placeholder="z. B. privat" autoComplete="off" />
                  <div className="flex items-center gap-2 sm:pt-6">
                    <CheckboxField name={`contacts.${index}.is_primary`} label="Primär" />
                    <Button type="button" variant="ghost" size="icon-sm" onClick={() => remove(index)} aria-label={`Kontakt ${index + 1} entfernen`}>
                      <Trash2 />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
            <Button type="button" variant="outline" size="sm" className="w-fit" onClick={() => append(emptyContact())}>
              <Plus aria-hidden="true" /> Kontakt hinzufügen
            </Button>
          </CardContent>
        </Card>

        <div className="flex flex-wrap gap-2">
          <Button type="submit" loading={form.formState.isSubmitting}>
            {create ? 'Anlegen' : 'Speichern'}
          </Button>
          <Button asChild variant="outline">
            {create ? (
              <Link to="/personen">Abbrechen</Link>
            ) : (
              <Link to="/personen/$partyId" params={{ partyId: String(party.id) }}>
                Abbrechen
              </Link>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
